import { useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { navigateFromStoredSteps, setStepsWithStatus } from "../lib/utils";
import { useLazyGetOnlineCheckoutOfferQuery, useLazyGetDashboardDetailsQuery } from "../redux/services/AuthApi";
import { useAuth } from "../hooks/UseAuth";
import { setOnlineCheckout } from "../redux/slice/UserSlice";

const OFFER_POLL_INTERVAL_MS = 30_000;

/** Sync checkout route on browser back — server step wins, then redirect to the matching step path. */
export function NavigationHandler() {
	const location = useLocation();
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const { user, onlineCheckout } = useAuth();
	const dispatch = useDispatch();
	const [getOnlineCheckoutOffer] = useLazyGetOnlineCheckoutOfferQuery();
	const [getDashboardDetails] = useLazyGetDashboardDetailsQuery();

	// Poll for offer changes while merchant is mid-checkout.
	// If UW reselects a new offer, redirect to /dashboard which re-syncs step state automatically.
	useEffect(() => {
		if (!onlineCheckout?.id || !onlineCheckout?.offer_id) return;
		if (!location.pathname.includes("online-checkout")) return;
		if (location.pathname.endsWith("/view-offer")) return;

		const poll = async () => {
			const result = await getDashboardDetails({});
			const freshOffer = result?.data?.data?.data?.[0];
			if (!freshOffer?.offer_id) return;

			const norm = (v: unknown) => parseFloat(String(v ?? "").replace(/,/g, ""));
			const offerIdChanged = String(freshOffer.offer_id) !== String(onlineCheckout.offer_id);
			const advanceChanged = freshOffer.selected_advance != null
				&& onlineCheckout.selected_advance != null
				&& norm(freshOffer.selected_advance) !== norm(onlineCheckout.selected_advance);
			if (!offerIdChanged && !advanceChanged) return;

			localStorage.removeItem("step");
			localStorage.removeItem("status");
			toast.info("Your offer has been updated. Please review the new terms.", {
				position: "top-right",
				autoClose: 8000,
			});
			navigate("/dashboard", { replace: true });
		};

		const id = setInterval(poll, OFFER_POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [
		location.pathname,
		onlineCheckout?.id,
		onlineCheckout?.offer_id,
		getDashboardDetails,
		navigate,
	]);

	useEffect(() => {
		// Only intervene on browser back/forward — forward clicks are handled by each step page.
		if (navigationType !== "POP") return;
		if (!location.pathname.includes("online-checkout")) return;
		if (!onlineCheckout?.id || !onlineCheckout?.offer_id) return;

		const isByzFlex = location.pathname.includes("/byzflex");
		const viewOfferPath = isByzFlex
			? "/online-checkout/byzflex/view-offer"
			: "/online-checkout/view-offer";
		if (location.pathname === viewOfferPath) return;

		let cancelled = false;

		(async () => {
			const dashResult = await getDashboardDetails({});
			if (cancelled) return;

			const freshOffer = dashResult?.data?.data?.data?.[0];
			const offerId = freshOffer?.offer_id ?? onlineCheckout.offer_id;
			if (freshOffer?.offer_id && String(freshOffer.offer_id) !== String(onlineCheckout.offer_id)) {
				dispatch(setOnlineCheckout(freshOffer));
			}

			const result = await getOnlineCheckoutOffer({
				lead_id: onlineCheckout.id,
				offer_id: offerId,
				email: user?.email,
			});
			if (cancelled) return;

			const data = result?.data?.data;
			const completedStep = data?.complete_step_info
				? parseInt(data.complete_step_info.step_number, 10)
				: 0;

			// Bank/identity done server-side but contract unsigned — send to view-offer entry point.
			if (data?.skip_to_contract && completedStep < 4) {
				setStepsWithStatus(1, 1);
				navigate(viewOfferPath, { replace: true });
				return;
			}

			if (data?.complete_step_info) {
				const step = parseInt(data.complete_step_info.step_number, 10);
				const status = parseInt(data.complete_step_info.status_number, 10);
				if (step > 0 && status > 0) setStepsWithStatus(step, status);

				// ENG-4373: Primary awaiting secondary — step=6 status=2 (review:completed)
				// on a multi-signer deal means the primary submitted but secondary hasn't signed.
				// Stay on primary-awaiting; the page itself polls and redirects to success when done.
				// (Backward compat: status=3 rows written by old code also route here via utils.ts.)
				if (step === 6 && status === 2 && data?.is_multi_principal) {
					navigate("/online-checkout/primary-awaiting", { replace: true });
					return;
				}
			}

			navigateFromStoredSteps(navigate, isByzFlex);
		})();

		return () => {
			cancelled = true;
		};
	}, [
		location.pathname,
		navigationType,
		onlineCheckout?.id,
		onlineCheckout?.offer_id,
		user?.email,
		dispatch,
		getDashboardDetails,
		getOnlineCheckoutOffer,
		navigate,
	]);

	return null;
}
